/** Diff-Import.
 *
 *  Das unspektakulärste Feature und wahrscheinlich das wichtigste: Sarah liest
 *  in Bookie weiter, Mario in Goodreads. Ohne einen Import, der nur die NEUEN
 *  Zeilen erkennt, ist diese App in drei Monaten veraltet.
 *
 *  Ablauf: Datei wählen → Spalten zuordnen (die beiden bekannten Formate werden
 *  erkannt) → Vorschau, was neu ist → auswählen → übernehmen. Gelöscht wird nie.
 */

import { useMemo, useRef, useState } from "react";
import Papa from "papaparse";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

type Format = "bookie" | "goodreads" | "frei";

/** Spaltennamen der beiden bekannten Exporte. */
const PROFILE: Record<Exclude<Format, "frei">, Record<string, string>> = {
  bookie: {
    isbn: "isbn",
    title: "title",
    author: "contributors",
    liste: "list_name",
    datum: "entry_created_at",
  },
  goodreads: {
    isbn: "ISBN13",
    isbn2: "ISBN",
    title: "Title",
    author: "Author",
    regal: "Exclusive Shelf",
    sterne: "My Rating",
    seiten: "Number of Pages",
    jahr: "Year Published",
    gelesen: "Date Read",
    hinzu: "Date Added",
    listen: "Bookshelves",
    id: "Book Id",
  },
};

function erkenneFormat(felder: string[]): Format {
  const set = new Set(felder);
  if (set.has("list_name") && set.has("contributors")) return "bookie";
  if (set.has("Exclusive Shelf") && set.has("Title")) return "goodreads";
  return "frei";
}

/** Excel-Panzerung `="978…"` und Trennzeichen entfernen. */
const entpanzern = (v: string) => (v || "").replace(/^="?|"?$/g, "").trim();

function zuZeilen(
  rows: Record<string, string>[],
  format: Format,
): api.ImportRow[] {
  if (format === "bookie") {
    // Eine Zeile je Listen-Mitgliedschaft — auf Bücher verdichten.
    // Erst alle Mitgliedschaften sammeln, DANN den Status bestimmen: sonst
    // entscheidet die zufällige Reihenfolge der Zeilen, und ein Buch, das nur
    // in einer eigenen Liste steht ("Blick reinwerfen"), gilt als gelesen.
    const SYSTEM = ["finished", "favorite", "currently_reading", "wishlist"];
    const nach = new Map<string, api.ImportRow>();
    const mitgliedschaften = new Map<string, Set<string>>();
    for (const r of rows) {
      const isbn = entpanzern(r[PROFILE.bookie.isbn]);
      if (!isbn) continue;
      const liste = (r[PROFILE.bookie.liste] || "").trim();
      const stamp = r[PROFILE.bookie.datum] || "";
      // 2000-01-01 ist Bookies Platzhalter für "kein Datum"
      const jahr = stamp.slice(0, 10) !== "2000-01-01" ? stamp.slice(0, 4) : null;

      const namen = mitgliedschaften.get(isbn) ?? new Set<string>();
      if (liste) namen.add(liste);
      mitgliedschaften.set(isbn, namen);

      const vorhanden = nach.get(isbn);
      if (!vorhanden) {
        nach.set(isbn, {
          isbn,
          title: (r[PROFILE.bookie.title] || "").trim(),
          author: (r[PROFILE.bookie.author] || "").trim(),
          read_year: jahr,
          fav: false,
          status: "read",
          lists: [],
          source_id: isbn,
        });
      } else if (jahr && (!vorhanden.read_year || jahr > vorhanden.read_year)) {
        vorhanden.read_year = jahr;
      }
    }
    for (const [isbn, buch] of nach) {
      const namen = mitgliedschaften.get(isbn) ?? new Set<string>();
      buch.fav = namen.has("favorite");
      buch.lists = [...namen].filter((n) => !SYSTEM.includes(n));
      buch.status = namen.has("currently_reading")
        ? "reading"
        : namen.has("wishlist")
          ? "want"
          : namen.has("finished")
            ? "read"
            : "want"; // nur in einer eigenen Liste — nie als gelesen markiert
    }
    return [...nach.values()];
  }

  if (format === "goodreads") {
    const p = PROFILE.goodreads;
    return rows
      .filter((r) => (r[p.title] || "").trim())
      .map((r) => {
        const datum = (r[p.gelesen] || r[p.hinzu] || "").trim();
        const sterne = Number(r[p.sterne] || 0);
        return {
          isbn: entpanzern(r[p.isbn]) || entpanzern(r[p.isbn2]) || null,
          title: (r[p.title] || "").trim(),
          author: (r[p.author] || "").trim(),
          status:
            r[p.regal] === "currently-reading"
              ? "reading"
              : r[p.regal] === "to-read"
                ? "want"
                : "read",
          read_year: datum ? datum.slice(0, 4) : null,
          stars: sterne > 0 ? sterne : null,
          pages: Number(r[p.seiten]) || null,
          pub_year: (r[p.jahr] || "").trim() || null,
          lists: (r[p.listen] || "")
            .split(",")
            .map((s) => s.trim())
            .filter(
              (s) => s && !["read", "currently-reading", "to-read"].includes(s),
            ),
          source_id: (r[p.id] || "").trim() || null,
        };
      });
  }

  // Freies Format: mindestens Titel, alles andere optional
  return rows
    .map((r) => {
      const keys = Object.keys(r);
      const finde = (kandidaten: string[]) =>
        keys.find((k) => kandidaten.some((c) => k.toLowerCase().includes(c)));
      const kTitel = finde(["titel", "title"]);
      const kAutor = finde(["autor", "author", "verfasser"]);
      const kIsbn = finde(["isbn"]);
      return {
        title: kTitel ? (r[kTitel] || "").trim() : "",
        author: kAutor ? (r[kAutor] || "").trim() : "",
        isbn: kIsbn ? entpanzern(r[kIsbn]) || null : null,
        status: "read",
      };
    })
    .filter((r) => r.title);
}

export function ImportSeite() {
  const { owner, modus, melde, neuLaden } = useStore();
  const dateiRef = useRef<HTMLInputElement>(null);
  const [format, setFormat] = useState<Format>("frei");
  const [dateiname, setDateiname] = useState("");
  const [zeilen, setZeilen] = useState<api.ImportRow[]>([]);
  const [diff, setDiff] = useState<api.ImportDiff | null>(null);
  const [gewaehlt, setGewaehlt] = useState<Set<number>>(new Set());
  const [laeuft, setLaeuft] = useState(false);

  const auswahl = useMemo(
    () => (diff ? diff.neu.filter((_, i) => gewaehlt.has(i)) : []),
    [diff, gewaehlt],
  );

  function datei(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setDateiname(f.name);
    setDiff(null);
    Papa.parse<Record<string, string>>(f, {
      header: true,
      skipEmptyLines: true,
      complete: (res) => {
        const felder = res.meta.fields ?? [];
        const erkannt = erkenneFormat(felder);
        setFormat(erkannt);
        const umgesetzt = zuZeilen(res.data, erkannt);
        setZeilen(umgesetzt);
        if (!umgesetzt.length) {
          melde(
            "In der Datei ließ sich keine verwertbare Zeile finden.",
            "fehler",
          );
        } else {
          melde(
            `${umgesetzt.length} Bücher gelesen (Format: ${erkannt === "frei" ? "unbekannt, geraten" : erkannt}).`,
            erkannt === "frei" ? "warnung" : "ok",
          );
        }
      },
      error: (err) => melde(`Datei nicht lesbar: ${err.message}`, "fehler"),
    });
  }

  async function pruefen() {
    if (modus !== "server") {
      melde("Der Import braucht den Server.", "fehler");
      return;
    }
    setLaeuft(true);
    try {
      const res = await api.analyzeImport(owner, zeilen);
      setDiff(res.data);
      setGewaehlt(new Set(res.data.neu.map((_, i) => i)));
      if (res.message)
        melde(res.message, res.status === "warnung" ? "warnung" : "ok");
    } catch (err) {
      melde(err instanceof Error ? err.message : String(err), "fehler");
    } finally {
      setLaeuft(false);
    }
  }

  async function uebernehmen() {
    setLaeuft(true);
    try {
      const res = await api.applyImport(owner, auswahl, dateiname, format);
      melde(
        res.message ?? `${res.data.added} übernommen.`,
        res.status === "warnung" ? "warnung" : "ok",
      );
      setDiff(null);
      setZeilen([]);
      setDateiname("");
      if (dateiRef.current) dateiRef.current.value = "";
      await neuLaden();
    } catch (err) {
      melde(err instanceof Error ? err.message : String(err), "fehler");
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <>
      <section style={{ maxWidth: "48ch" }}>
        <h2 className="titelzeile">Nachtragen</h2>
        <p className="lauftext">
          Neuen Export hochladen — erkannt werden die Bookie- und die
          Goodreads-Spalten. Gezeigt wird nur, was noch nicht im Regal steht. Es
          wird nichts überschrieben und nie etwas gelöscht.
        </p>
      </section>

      <div className="karte" style={{ marginTop: 26 }}>
        <span className="feldname">CSV-Datei</span>
        <input
          ref={dateiRef}
          type="file"
          accept=".csv,text/csv"
          onChange={datei}
        />
        {zeilen.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              marginTop: 14,
              flexWrap: "wrap",
            }}
          >
            <span className="klein">
              {dateiname} · {zeilen.length} Bücher · Format {format}
            </span>
            <button
              className="knopf stark"
              disabled={laeuft}
              onClick={() => void pruefen()}
            >
              {laeuft ? "prüft …" : "Prüfen, was neu ist"}
            </button>
          </div>
        )}
      </div>

      {diff && (
        <>
          <h3 className="brettname">
            Neu <em>{diff.neu.length}</em>
          </h3>
          <p className="lauftext" style={{ fontSize: 14 }}>
            Bestand vorher: {diff.bestand} Bände. Abgewählte Zeilen bleiben
            draußen.
          </p>
          <div style={{ display: "grid", gap: 6, marginTop: 12 }}>
            {diff.neu.map((r, i) => (
              <label
                key={`${r.isbn ?? r.title}-${i}`}
                className="karte"
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  padding: "9px 14px",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  style={{ width: 16, height: 16, flex: "0 0 auto" }}
                  checked={gewaehlt.has(i)}
                  onChange={(e) => {
                    const neu = new Set(gewaehlt);
                    if (e.target.checked) neu.add(i);
                    else neu.delete(i);
                    setGewaehlt(neu);
                  }}
                />
                <span style={{ fontFamily: "var(--book)", fontSize: 15 }}>
                  {r.title}
                  <span className="klein" style={{ marginLeft: 10 }}>
                    {r.author}
                    {r.read_year ? ` · ${r.read_year}` : ""}
                    {r.fav ? " · ★" : ""}
                  </span>
                </span>
              </label>
            ))}
          </div>

          {diff.verdacht.length > 0 && (
            <>
              <h3 className="brettname">
                Zu bestätigen <em>{diff.verdacht.length}</em>
              </h3>
              <p className="lauftext" style={{ fontSize: 14 }}>
                Diese Titel ähneln vorhandenen Büchern. Achtung: Reihenbände
                unterscheiden sich oft nur durch eine Zahl — automatisch
                zusammenführen würde Bücher fressen.
              </p>
              <div style={{ display: "grid", gap: 6, marginTop: 10 }}>
                {diff.verdacht.map((v, i) => (
                  <div
                    key={i}
                    className="karte"
                    style={{ padding: "9px 14px" }}
                  >
                    <span style={{ fontFamily: "var(--book)", fontSize: 15 }}>
                      {v.row.title}
                    </span>
                    <span className="klein" style={{ marginLeft: 10 }}>
                      ähnlich zu „{v.kandidat}" ({Math.round(v.naehe * 100)} %)
                    </span>
                    <button
                      className="knopf"
                      style={{ marginLeft: 12 }}
                      onClick={() => {
                        setDiff({
                          ...diff,
                          neu: [...diff.neu, v.row],
                          verdacht: diff.verdacht.filter((_, j) => j !== i),
                        });
                        setGewaehlt(new Set([...gewaehlt, diff.neu.length]));
                      }}
                    >
                      Ist ein anderes Buch — übernehmen
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {diff.bekannt.length > 0 && (
            <p className="klein" style={{ marginTop: 22 }}>
              {diff.bekannt.length} Zeilen stehen schon im Regal und werden
              übersprungen.
            </p>
          )}
          {diff.fehlerhaft.length > 0 && (
            <div
              className="karte"
              style={{ borderLeft: "3px solid var(--warn)", marginTop: 14 }}
            >
              <strong>{diff.fehlerhaft.length} Zeilen sind fehlerhaft:</strong>
              <ul className="lauftext" style={{ fontSize: 14 }}>
                {diff.fehlerhaft.slice(0, 8).map((f, i) => (
                  <li key={i}>
                    {f.row.title || "(ohne Titel)"} — {f.grund}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div
            style={{
              display: "flex",
              gap: 12,
              marginTop: 24,
              flexWrap: "wrap",
            }}
          >
            <button
              className="knopf stark"
              disabled={laeuft || !auswahl.length}
              onClick={() => void uebernehmen()}
            >
              {laeuft ? "übernimmt …" : `${auswahl.length} Bücher übernehmen`}
            </button>
            <button className="knopf" onClick={() => setDiff(null)}>
              Abbrechen
            </button>
          </div>
        </>
      )}
    </>
  );
}
